import React, { useState, useEffect, useRef } from 'react';
import {
  AlertTriangleIcon,
  CheckCircleIcon,
  RotateCcw,
  MousePointerClick,
  Save,
  X,
  Upload,
} from 'lucide-react';
import { Spin } from 'antd';
import { api } from '../../../helpers/backendHelper';
import { useUpdateInstanceFramePixelSpacing } from '../../../helpers/api/instance/instance';

// --- Helper UI Components (small local variants) ---
const Alert = ({ children, variant = 'default', className = '' }: any) => {
  const variants: Record<string, string> = {
    default:
      'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-400',
    destructive:
      'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-400',
    success:
      'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-400',
  };
  return (
    <div
      className={`px-4 py-3 rounded-lg border text-sm flex items-center gap-3 ${variants[variant]} ${className}`}
    >
      {children}
    </div>
  );
};

const Button = ({
  children,
  onClick,
  disabled = false,
  variant = 'primary',
  className = '',
}: any) => {
  const baseClasses =
    'inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm';
  const variants: Record<string, string> = {
    primary:
      'bg-blue-600 dark:bg-blue-700 text-white hover:bg-blue-700 dark:hover:bg-blue-800 focus:ring-blue-500',
    secondary:
      'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-gray-500',
    destructive:
      'bg-red-600 dark:bg-red-700 text-white hover:bg-red-700 dark:hover:bg-red-800 focus:ring-red-500',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};
// --- End Helper Components ---

export default function PixelSpacingModal({
  isOpen,
  onClose,
  onSave,
  frameImage,
  studyUid,
  serieUid,
  instanceUid,
  frameIndex,
  onUpdateSuccess,
}: any) {
  const [selectedPoints, setSelectedPoints] = useState<
    Array<{ x: number; y: number }>
  >([]);
  const [detectionResult, setDetectionResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const updateInstanceFramePixelSpacingMutation =
    useUpdateInstanceFramePixelSpacing(
    (response) => {
      setIsUpdating(false);
      const fallbackPixelSpacing =
        typeof detectionResult?.pixel_spacing_cm_per_pixel === 'number'
          ? [
              detectionResult.pixel_spacing_cm_per_pixel * 10,
              detectionResult.pixel_spacing_cm_per_pixel * 10,
            ]
          : null;
      const storedPixelSpacing =
        typeof frameIndex === 'number' &&
        frameIndex >= 0 &&
        Array.isArray(response?.data?.pixel_spacing?.[frameIndex])
          ? response.data.pixel_spacing[frameIndex]
          : fallbackPixelSpacing;
      if (
        storedPixelSpacing &&
        instanceUid &&
        typeof frameIndex === 'number' &&
        frameIndex >= 0
      ) {
        onUpdateSuccess?.({
          sop_instance_uid: instanceUid,
          frame_index: frameIndex,
          pixel_spacing: storedPixelSpacing,
        });
      }
      onClose();
    },
    (response, error) => {
      setIsUpdating(false);
      setError('Failed to update frame pixel spacing');
    }
  );

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [canvasDims, setCanvasDims] = useState({ width: 0, height: 0 });
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // Fullscreen/enhanced interaction states
  const [zoom, setZoom] = useState(1); // 1.0 == natural size
  const [cropMode, setCropMode] = useState(false);
  const [cropRect, setCropRect] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);
  const croppingRef = useRef<{ startX: number; startY: number } | null>(null);

  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setSelectedPoints([]);
      setDetectionResult(null);
      setError(null);
      setIsProcessing(false);
      setZoom(1);
      setCropMode(false);
      setCropRect(null);
      croppingRef.current = null;
    }
  }, [isOpen, frameImage]);

  // Draw full canvas (only view)
  useEffect(() => {
    if (!isOpen || !frameImage || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      setCanvasDims({ width: img.naturalWidth, height: img.naturalHeight });

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      // scale visually
      ctx.scale(zoom, zoom);
      ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);

      // Draw selected polygon & points
      if (selectedPoints.length > 0) {
        ctx.strokeStyle = '#3B82F6';
        ctx.lineWidth = 3 / Math.max(zoom, 0.001);
        ctx.beginPath();
        ctx.moveTo(selectedPoints[0].x, selectedPoints[0].y);
        for (let i = 1; i < selectedPoints.length; i++) {
          ctx.lineTo(selectedPoints[i].x, selectedPoints[i].y);
        }
        if (selectedPoints.length === 4) {
          ctx.closePath();
          ctx.fillStyle = 'rgba(59,130,246,0.12)';
          ctx.fill();
        }
        ctx.stroke();
        selectedPoints.forEach((pt) => {
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, 8 / Math.max(zoom, 0.001), 0, Math.PI * 2);
          ctx.fillStyle = '#fff';
          ctx.fill();
          ctx.lineWidth = 2 / Math.max(zoom, 0.001);
          ctx.strokeStyle = '#3B82F6';
          ctx.stroke();
        });
      }

      // Crop rect preview
      if (cropRect) {
        ctx.strokeStyle = 'rgba(250,130,49,0.95)';
        ctx.lineWidth = 2 / Math.max(zoom, 0.001);
        ctx.setLineDash([6 / Math.max(zoom, 0.001), 4 / Math.max(zoom, 0.001)]);
        ctx.strokeRect(cropRect.x, cropRect.y, cropRect.w, cropRect.h);
        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(250,130,49,0.08)';
        ctx.fillRect(cropRect.x, cropRect.y, cropRect.w, cropRect.h);
      }

      ctx.restore();
    };
    img.src = frameImage;
  }, [isOpen, frameImage, selectedPoints, zoom, cropRect]);

  const getPointAt = (x: number, y: number) => {
    for (let i = 0; i < selectedPoints.length; i++) {
      const pt = selectedPoints[i];
      if (Math.hypot(pt.x - x, pt.y - y) < 12) return i;
    }
    return null;
  };

  const coordsFromEvent = (
    e: React.MouseEvent,
    canvas: HTMLCanvasElement,
    zoomFactor = 1
  ) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.clientX - rect.left) * scaleX) / zoomFactor;
    const y = ((e.clientY - rect.top) * scaleY) / zoomFactor;
    return { x, y };
  };

  // Full canvas handlers
  const handleDown = (e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    const { x, y } = coordsFromEvent(e, canvasRef.current, zoom);

    if (cropMode) {
      croppingRef.current = { startX: x, startY: y };
      setCropRect({ x, y, w: 0, h: 0 });
      setDragIndex(null);
    } else {
      setDragIndex(getPointAt(x, y));
    }
  };

  const handleMove = (e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    const { x, y } = coordsFromEvent(e, canvasRef.current, zoom);

    if (cropMode && croppingRef.current) {
      const sx = croppingRef.current.startX;
      const sy = croppingRef.current.startY;
      const rx = Math.min(sx, x);
      const ry = Math.min(sy, y);
      const rw = Math.abs(x - sx);
      const rh = Math.abs(y - sy);
      setCropRect({ x: rx, y: ry, w: rw, h: rh });
    } else if (dragIndex !== null) {
      setSelectedPoints((points) =>
        points.map((pt, i) => (i === dragIndex ? { x, y } : pt))
      );
    }
  };

  const handleUp = (e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    const { x, y } = coordsFromEvent(e, canvasRef.current, zoom);

    if (cropMode && croppingRef.current) {
      const sx = croppingRef.current.startX;
      const sy = croppingRef.current.startY;
      const rx = Math.min(sx, x);
      const ry = Math.min(sy, y);
      const rw = Math.abs(x - sx);
      const rh = Math.abs(y - sy);
      setCropRect({ x: rx, y: ry, w: rw, h: rh });
      croppingRef.current = null;
    } else {
      if (selectedPoints.length < 4 && dragIndex === null) {
        setSelectedPoints((prev) => [...prev, { x, y }]);
      }
      setDragIndex(null);
    }
  };

  const handleDetectPixelSpacing = async () => {
    if (!frameImage || selectedPoints.length !== 4) {
      setError('Please select 4 reference points before detecting.');
      return;
    }
    try {
      setIsProcessing(true);
      setError(null);

      const response = await fetch(frameImage);
      const blob = await response.blob();

      const formData = new FormData();
      formData.append('image', blob, 'capture.jpg');
      formData.append('dashes_polygon', JSON.stringify(selectedPoints));

      const result = await api.post('/pixel_spacing_detection', formData);

      if (result && typeof result.pixel_spacing_cm_per_pixel === 'number') {
        setDetectionResult(result);
      } else {
        throw new Error(result?.error || 'Invalid response from server.');
      }
    } catch (err: any) {
      setError(err.message || 'Detection failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmAndSave = async () => {
    if (!detectionResult) {
      setError('Run detection first.');
      return;
    }
    const pixelSpacingValueCm = detectionResult.pixel_spacing_cm_per_pixel;
    setIsProcessing(true);

    const crop = cropRect
      ? [
          Math.round(cropRect.x),
          Math.round(cropRect.y),
          Math.round(cropRect.x + cropRect.w),
          Math.round(cropRect.y + cropRect.h),
        ]
      : [0, 0, canvasDims.width, canvasDims.height];

    const payload = {
      depth_cm: detectionResult.depth_cm ?? null,
      zoom_factor: zoom,
      pixel_spacing_cm_per_pixel: pixelSpacingValueCm,
      crop_box: crop,
      serieUid,
    };

    try {
      await Promise.resolve(onSave(payload));
    } catch (err) {
      // fallback older signature
      try {
        await Promise.resolve(onSave(pixelSpacingValueCm));
      } catch (e) {
        setError('Failed to save calibration.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdatePixelSpacing = async () => {
    if (!detectionResult) {
      setError('Detection result is missing.');
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      if (!instanceUid) {
        setError('Instance UID is missing.');
        setIsUpdating(false);
        return;
      }

      if (!studyUid) {
        setError('Study UID is missing.');
        setIsUpdating(false);
        return;
      }

      if (typeof frameIndex !== 'number' || Number.isNaN(frameIndex) || frameIndex < 0) {
        setError('Frame index is missing.');
        setIsUpdating(false);
        return;
      }

      const pixelSpacingValueCm = detectionResult.pixel_spacing_cm_per_pixel;
      await updateInstanceFramePixelSpacingMutation.mutateAsync({
        sop_instance_uid: instanceUid,
        study_instance_uid: studyUid,
        frame_index: frameIndex,
        pixel_spacing_value: pixelSpacingValueCm,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to update pixel spacing.');
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onMouseDown={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 p-4 rounded shadow-lg w-full max-w-6xl max-h-[92vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold dark:text-gray-200">
              Pixel Spacing — Large View
            </h3>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Click to add up to 4 points. Drag to refine. Toggle crop to draw a
              crop box.
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="inline-flex items-center bg-gray-100 dark:bg-gray-700 rounded px-2 py-1 gap-2">
              <button
                onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                className="px-2"
              >
                -
              </button>
              <div className="text-sm">{zoom.toFixed(2)}x</div>
              <button
                onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
                className="px-2"
              >
                +
              </button>
            </div>

            <Button
              variant="secondary"
              onClick={() => {
                setCropMode((m) => !m);
              }}
            >
              {cropMode ? 'Exit Crop' : 'Crop'}
            </Button>

            <Button
              variant="secondary"
              onClick={() => {
                setSelectedPoints([]);
                setCropRect(null);
              }}
            >
              <RotateCcw size={16} /> Reset
            </Button>

            <button
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={onClose}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-2">
            <Alert variant="destructive">
              <AlertTriangleIcon /> {error}
            </Alert>
          </div>
        )}

        <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-2 rounded">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              cursor: cropMode
                ? 'crosshair'
                : selectedPoints.length < 4
                  ? 'crosshair'
                  : 'grab',
            }}
            onMouseDown={handleDown}
            onMouseMove={handleMove}
            onMouseUp={handleUp}
          />
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <div>Points: {selectedPoints.length} / 4</div>
            <div>
              Crop:{' '}
              {cropRect
                ? `${Math.round(cropRect.x)},${Math.round(cropRect.y)},${Math.round(cropRect.w)}x${Math.round(cropRect.h)}`
                : 'none'}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={handleDetectPixelSpacing}
              disabled={selectedPoints.length !== 4 || isProcessing}
            >
              {isProcessing ? (
                <Spin />
              ) : (
                <>
                  <MousePointerClick size={14} className="mr-2" /> Detect
                </>
              )}
            </Button>

            <Button
              onClick={handleConfirmAndSave}
              disabled={!detectionResult || isProcessing}
            >
              <Save size={14} className="mr-2" /> Save
            </Button>

            <Button
              variant="secondary"
              onClick={handleUpdatePixelSpacing}
              disabled={!detectionResult || isUpdating}
              title="Update this frame pixel spacing in the instance array"
            >
              {isUpdating ? (
                <Spin />
              ) : (
                <>
                  <Upload size={14} className="mr-2" />
                  Update Frame
                </>
              )}
            </Button>
          </div>
        </div>

        {detectionResult && (
          <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-700 rounded border dark:border-gray-600">
            <div className="text-sm text-gray-500">
              Calculated Pixel Spacing
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {detectionResult.pixel_spacing_cm_per_pixel.toFixed(5)}
            </div>
            <div className="text-sm text-gray-500">cm / pixel</div>
          </div>
        )}
      </div>
    </div>
  );
}
